<template>
    <div
        v-click-outside="close"
        class="minicart"
        :class="{
            [`theme-${theme}`]: true,
            'open': isOpen,
        }"
    >
        <button class="label" @click="isOpen = !isOpen">
            <DSIcon code="cart" class="icon" :size="$bp.bps.isDesktop ? 'l' : 'xl'" />
            <span class="sum" :aria-label="$t('minicart.cartSum', {sum: totals.totalWithTax})">{{ $cms.formatPrice(totals.totalWithTax) }}</span>
            <span class="count" :aria-label="$t('minicart.cartContains', {amount: cartAmounts })">{{ cartAmounts }}</span>
        </button>
        <div
            class="options"
            :inert="!isOpen"
        >
            <div v-if="cart.length" class="minicart-container">
                <div
                    v-for="product in cart"
                    :key="product.id"
                    class="mini-cart-product"
                    :class="{'marked-for-delete': idMarkedForDelete === product.code}"
                >
                    <span class="product-name">{{ product.name }}</span>
                    <div class="product-details">
                        <span class="product-amount">x {{ product.amount }}</span>
                        <span class="product-price">{{ $cms.formatPrice(product.price) }}</span>
                    </div>
                    <button v-if="idMarkedForDelete === product.code" class="product-delete">
                        <DSIcon
                            code="undo"
                            size="xs"
                            @click.stop="undo(product.id)"
                        />
                    </button>
                    <button v-else class="product-delete">
                        <DSIcon
                            code="close"
                            size="xs"
                            @click.stop="onDeleteProduct(product.code)"
                        />
                    </button>

                    <div class="undo-bar" :class="{counting: idMarkedForDelete === product.code}" />
                </div>
                <NuxtLink
                    :aria-label="$t('minicart.cartProceed')"
                    class="ds-btn ds-btn-large ds-btn--primary checkout-btn"
                    :to="props.checkoutTo"
                >
                    <span>{{ i18n.t('minicart.checkout') }}</span>
                    <DSIcon code="chevron-right" size="xs" />
                </NuxtLink>
            </div>
            <div v-else class="minicart-container">
                <div class="mini-cart-product empty">
                    {{ $t('minicart.empty') }}
                </div>
            </div>
        </div>
        <Teleport v-if="useModalStore().name === 'firstInCart'" :to="useModalStore().modalTarget">
            <div class="ds-modal-content ds-modal-first-in-cart">
                <div class="head">
                    <button class="close-btn" @click="useModalStore().unsetNameAndClose">
                        <DSIcon
                            code="close"
                            size="xs"
                        />
                    </button>
                </div>
                <div class="body">
                    <div class="ds-container">
                        <h1 class="h2">{{ $t('addToCartModal.firstInCartHeading') }}</h1>
                        <h2 class="h3">{{ cart[0].name }}</h2>
                        <h2 class="h3">{{ $cms.formatPrice(cart[0].price) }}</h2>
                        <div class="checkout-modal-buttons">
                            <NuxtLink class="ds-btn ds-btn--primary ds-btn--large ds-btn--full" :to="$t('minicart.toCheckout')" @click="useModalStore().unsetNameAndClose()">{{ $t('addToCartModal.toCheckout') }}</NuxtLink>
                            <NuxtLink class="ds-btn ds-btn--secondary ds-btn--large ds-btn--full" @click="useModalStore().unsetNameAndClose()">{{ $t('addToCartModal.continueShopping') }}</NuxtLink>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>
<script setup>
import { ref, inject, watch, storeToRefs, useI18n, useRoute, useNuxtApp, useAnalytics } from '#imports';
import vClickOutside from '~dsc/directives/click-outside';
import { useCartStore } from '~dsc/stores/cms/cart';
import { useUserStore } from '~dsc/stores/cms/user';
import { useModalStore } from '~dsc/stores/modal';

const { $analytics, $bp } = useNuxtApp();
const { cart, totals, cartAmounts } = storeToRefs(useCartStore());
const { loggedIn } = storeToRefs(useUserStore());
const { gtm } = useAnalytics();

const props = defineProps({
    checkoutTo: {
        type: String,
        required: false,
        default: '/kassa',
    },
});

const theme = inject('theme');
const i18n = useI18n();
const isOpen = ref(false);
const idMarkedForDelete = ref(null);
const undoed = ref(false);
const route = useRoute();
const timeout = ref(null);
const cartStore = useCartStore();

const close = () => {
    isOpen.value = false;
};

watch(isOpen, to => {
    if (to === true) {
        gtm().trackEvent({
            event: 'view_cart',
            action: 'click',
            currency: $analytics.getCurrency(),
            value: totals.value.totalWithoutTax,
            items: [...cart.value.map(product => $analytics.productToAnalytics(product))],
            event_function: 'general',
            logged_in_status: loggedIn.value ? 'logged_in' : 'logged_out',
            user_type: 'consumer',
        });
    }
});

watch(() => route.path, () => {
    isOpen.value = false;
});

const productAnalytics = product => ({
    event: 'remove_from_cart',
    event_function: 'general',
    action: 'click',
    user_type: 'consumer',
    logged_in_status: loggedIn.value ? 'logged_in' : 'logged_out',
    currency: $analytics.getCurrency(product),
    value: $analytics.priceWithoutTax(product),
    items: [$analytics.productToAnalytics(product)],
});

const onDeleteProduct = id => {
    clearTimeout(timeout.value);

    if (idMarkedForDelete.value) {
        gtm().trackEvent(productAnalytics(cartStore.getCartProductByCode(idMarkedForDelete.value)));
        cartStore.deleteProduct(idMarkedForDelete.value);
    }
    idMarkedForDelete.value = id;

    timeout.value = setTimeout(() => {
        if (undoed.value) {
            undoed.value = false;
            idMarkedForDelete.value = null;

            return;
        }
        if (idMarkedForDelete.value === id) {
            gtm().trackEvent(productAnalytics(cartStore.getCartProductByCode(id)));
            cartStore.deleteProduct(id);
        }
        idMarkedForDelete.value = null;
    }, 5400);
};

const undo = () => {
    undoed.value = true;
    clearTimeout(timeout.value);
    undoed.value = false;
    idMarkedForDelete.value = null;
};

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            minicart: {
                checkout: 'Siirry maksamaan',
                empty: 'Ostoskorisi on tyhjä.',
                toCheckout: '/kassa',
                cartContains: 'Ostoskorissasi on {amount} tuotetta.',
                cartSum: 'Kokonaissumma on {sum} euroa.',
                cartProceed: 'Nappia klikkaamalla jatkat ostoskoriisi',
            },
        },
        'en-EU': {
            minicart: {
                checkout: 'Siirry maksamaan',
                empty: 'Ostoskorisi on tyhjä.',
                toCheckout: '/checkout',
                cartContains: 'Your shopping cart contains {{amount}} products.',
                cartSum: 'Total sum is {{sum}} euros.',
                cartProceed: 'By clicking this button, you will proceed with your shopping.',
            },
        },
    },
});
</script>
<style scoped>
.minicart {
  display: inline-flex;
  position: relative;
  background-color: var(--color-base-grey-100);
  padding: 0.5rem;
  border-radius: 0.5rem;
}
.minicart.theme-black .label {
  color: var(--color-base-white);
}
.minicart .label {
  display: grid;
  align-items: center;
  text-transform: uppercase;
  padding: 0;
  min-height: 1.5rem;
  min-width: 150px;
  font-weight: 700;
  grid-template-columns: 1fr 3fr 1fr;
}
.minicart .label .icon {
  transition: all 0.2s ease-in-out;
  position: relative;
  transform: rotate(0) translateY(0);
  transform-origin: center center;
}
.minicart .label .sum {
  padding: 0 0.25rem;
}
.minicart .label .count {
  background-color: var(--color-warning-500);
  padding: 0.125rem 0.5rem;
  border-radius: 0.5rem;
}
.minicart .label:focus-visible {
  outline: -webkit-focus-ring-color auto 1px;
}
.minicart .options {
  transition: all 0.2s ease-in-out;
  position: absolute;
  bottom: -0.5rem;
  left: 50%;
  display: grid;
  transform: translateX(calc(-100% + 1rem)) translateY(100%) scaleY(0);
  transform-origin: center top;
  background: var(--color-base-white);
  z-index: 1;
}
@media (max-width: 1199px) {
  .minicart .options {
    left: 4rem;
  }
}
.minicart .options::before {
  content: "";
  border: 1px solid var(--color-base-grey-200);
  border-bottom: 0;
  border-right: 0;
  background: var(--color-base-white);
  width: 1rem;
  height: 1rem;
  position: absolute;
  top: -1px;
  right: 0.5rem;
  transform: rotate(45deg) translateY(2px);
}
@media (max-width: 1199px) {
  .minicart .options::before {
    right: 3.25rem;
  }
}
.minicart .options .minicart-container {
  border-radius: 0.25rem;
  margin-top: 0.5rem;
  padding: 1rem;
  box-shadow: var(--panel-box-shadow);
  border: 1px solid var(--color-base-grey-200);
  background-color: var(--color-base-white);
}
.minicart .options .minicart-container .mini-cart-product {
  border-bottom: 1px solid var(--color-base-grey-200);
  width: 250px;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: 1fr 1fr;
  padding: 0.5rem;
  font-weight: 700;
  font-size: 0.875rem;
}
.minicart .options .minicart-container .mini-cart-product .product-name {
  grid-column-start: 1;
  grid-row-start: 1;
}
.minicart .options .minicart-container .mini-cart-product .product-details {
  grid-column-start: 1;
  grid-row-start: 2;
  font-size: 0.75rem;
  color: var(--color-base-grey-600);
}
.minicart .options .minicart-container .mini-cart-product .product-details .product-amount {
  padding-right: 0.5rem;
  margin-right: 0.5rem;
  border-right: 1px solid var(--color-base-grey-200);
}
.minicart .options .minicart-container .mini-cart-product.marked-for-delete {
  text-decoration: line-through;
  color: var(--color-base-grey-300);
}
.minicart .options .minicart-container .mini-cart-product.marked-for-delete .product-details {
  color: var(--color-base-grey-300);
}
.minicart .options .minicart-container .mini-cart-product .product-delete {
  grid-column-start: 2;
  grid-row-start: 1;
  justify-self: end;
  cursor: pointer;
}
.minicart .options .minicart-container .mini-cart-product:focus-visible {
  outline: -webkit-focus-ring-color auto 1px;
}
.minicart .options .minicart-container .mini-cart-product.empty {
  border: none;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  text-align: center;
}
.minicart .options .minicart-container .undo-bar {
  width: 0;
  height: 0.25rem;
  background-color: var(--color-base-grey-300);
  grid-row-start: 3;
  grid-column-start: 1;
  grid-column-end: 3;
}
.minicart .options .minicart-container .undo-bar.counting {
  width: 100%;
  animation-name: countdown;
  animation-duration: 5s;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}
@keyframes countdown {
  from {
    width: 100%;
  }
  to {
    width: 0;
  }
}
.minicart .options .minicart-container .checkout-btn {
  margin-top: 1rem;
  border-radius: 0.5rem;
  height: 3rem;
  display: grid;
  justify-content: center;
  align-items: center;
  background-color: var(--color-primary-500);
  font-weight: 700;
}
.minicart .options .minicart-container .checkout-btn:hover {
  background-color: var(--color-primary-700);
}
.minicart .options .minicart-container .checkout-btn :nth-child(1) {
  display: block;
  grid-column-start: 2;
}
.minicart .options .minicart-container .checkout-btn :nth-child(2) {
  display: block;
  grid-column-start: 3;
  padding-left: 0.5rem;
  width: 1.5rem;
}
.minicart.open .options {
  transform: translateX(calc(-100% + 1rem)) translateY(100%) scaleY(1);
}
@media (max-width: 1199px) {
  .minicart {
    background-color: var(--color-base-white);
    margin: 0;
    padding: 0;
  }
  .minicart .label {
    grid-template-columns: 1fr;
    margin: 0;
    padding: 0;
    height: 2rem;
    width: 2rem;
    min-width: 0;
    justify-items: center;
  }
  .minicart .label .sum {
    display: none;
  }
  .minicart .label .count {
    position: absolute;
    top: 0.75rem;
    left: 1.25rem;
    font-size: 0.75rem;
  }
}

.ds-modal .ds-modal-first-in-cart {
  max-width: 35rem;
}
.ds-modal .ds-modal-first-in-cart .ds-container {
  padding: 3rem;
}
.ds-modal .ds-modal-first-in-cart .ds-container .h2 {
  font-size: 1.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 2rem;
}
.ds-modal .ds-modal-first-in-cart .ds-container .h3 {
  font-weight: 800;
  font-size: 2.25rem;
  line-height: 1.5rem;
  text-align: center;
}
.ds-modal .ds-modal-first-in-cart .ds-container .checkout-modal-buttons * {
  margin: 0.5rem 0;
}
</style>
